import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import Footer from "../Layout/Footer";
import Header from "../Layout/Header";
import Pagination from "../Layout/Pagination";
import {
  get,
  postAPI,
  putAPI,
  uploadImageAPI,
} from "../../ApiService";
import { DEFAULT_USER_AVATAR } from "../../utils/userProfile";
import { masterData } from "../../Api/Master";

const isUserActive = (row) => {
  const slug = (row?.statusSlug || "").toLowerCase();
  const txt = (row?.statusTxt || "").toLowerCase();
  return (
    slug === "verified" ||
    slug === "active" ||
    txt === "active" ||
    txt === "verified"
  );
};

function ActiveUser() {
  useEffect(() => {
    document.title = "Users - Mindforce";
  });

  const [errors, setErrors] = useState({});
  const [searchKeyword, setSearchKeyword] = useState();
  const [pageInfo, setPagination] = useState({});
  const [users, setUsers] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [user, setUser] = useState({});
  const [displayFormMode, setDisplayFormMode] = useState("");
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(DEFAULT_USER_AVATAR);
  const [avatarUploading, setAvatarUploading] = useState(false);

  const MAX_AVATAR_SIZE_BYTES = 1 * 1024 * 1024; // 1 MB
  const MAX_AVATAR_DIMENSION = 500;

  const closeDrawer = () => {
    setUser({});
    setDisplayFormMode("");
    setErrors({});
    setAvatarPreview(DEFAULT_USER_AVATAR);
    setAvatarUploading(false);
  };

  const openAddDrawer = () => {
    setErrors({});
    setUser({});
    setAvatarPreview(DEFAULT_USER_AVATAR);
    setDisplayFormMode("add");
  };

  const openEditDrawer = (row) => {
    setErrors({});
    setUser(row);
    setAvatarPreview(row?.profileImg || DEFAULT_USER_AVATAR);
    setDisplayFormMode("edit");
  };

  useEffect(() => {
    if (!displayFormMode) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [displayFormMode]);

  const getRoles = async () => {
    let data = {
      page: pageInfo.current_page,
      recPerPage: 500,
      q: "",
      func: "",
    };
    let res = await get("/urm/role/list", data);
    if (res.status === 1) {
      setRoles(res.payload);
    }
  };

  useEffect(() => {
    getRoles();
  }, []);

  const getTeams = async () => {
    let res = await masterData({ moduleName: "team" });
    if (res?.status === 1) {
      setTeams(res.payload || []);
    } else {
      setTeams([]);
    }
  };

  useEffect(() => {
    getTeams();
  }, []);

  const getList = async () => {
    setListLoading(true);
    try {
      const searchEl = document.getElementById("searchKeyWord");
      if (searchEl) setSearchKeyword(searchEl.value);
      let payloadData = {
        q: searchKeyword ? searchKeyword : "",
        page: pageInfo.current_page ? pageInfo.current_page : 1,
        recPerPage: 50,
      };
      let res = await get("/urm/user/list/", payloadData);
      if (res.status === 1) {
        setUsers(res.payload || []);
        setPagination(res.paginationInfo);
      } else {
        setUsers([]);
      }
    } catch (err) {
      setUsers([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    getList();
  }, []);

  const changePage = (p) => {
    pageInfo.current_page = p;
    getList();
  };

  const submitUser = async (e) => {
    e.preventDefault();
    setErrors({});

    const formData = new FormData(e.target);
    let formInput = {};
    for (const [key, value] of formData) {
      formInput[key] = value;
    }

    let res = {};
    if (!user || !user.ID) res = await postAPI("/urm/user/save", formInput);
    else res = await putAPI("/urm/user/update/" + user.ID, formInput);

    if (res.status === 1) {
      closeDrawer();
      getList();
      Swal.fire({ text: res.message, timer: 2500 });
    } else {
      setErrors({ saveUser: res.message });
    }
  };

  const getImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const size = { width: img.width, height: img.height };
        URL.revokeObjectURL(objectUrl);
        resolve(size);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Invalid image file"));
      };
      img.src = objectUrl;
    });

  const handleChange = async (e) => {
    const files = e.target.files;
    const file = files?.[0];
    if (!file) return;

    if (!file.type?.startsWith("image/")) {
      e.target.value = "";
      setErrors({ saveUser: "Please upload a valid image file." });
      return;
    }

    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      e.target.value = "";
      setErrors({ saveUser: "Avatar size must be 1 MB or less." });
      return;
    }

    try {
      const { width, height } = await getImageDimensions(file);
      if (width > MAX_AVATAR_DIMENSION || height > MAX_AVATAR_DIMENSION) {
        e.target.value = "";
        setErrors({
          saveUser: `Avatar dimensions must not exceed ${MAX_AVATAR_DIMENSION}*${MAX_AVATAR_DIMENSION} px.`,
        });
        return;
      }
    } catch (err) {
      e.target.value = "";
      setErrors({ saveUser: "Could not read image. Please try another file." });
      return;
    }

    setErrors({});
    setAvatarUploading(true);
    try {
      let res = await uploadImageAPI("/upload/common/", files);
      if (res.status === 1) {
        const profileImgInput = document.getElementById("profileImg");
        if (profileImgInput) profileImgInput.value = res.url;
        setAvatarPreview(res.url);
        const preview = document.getElementById("imageView");
        if (preview) preview.src = res.url;
      } else {
        setErrors({ saveUser: res.message || "Avatar upload failed." });
      }
    } catch (err) {
      setErrors({ saveUser: "Avatar upload failed. Please try again." });
    } finally {
      setAvatarUploading(false);
    }
  };

  const updateUserStatus = async (row) => {
    if (!row?.ID || statusUpdatingId) return;
    const nextStatus = isUserActive(row) ? 0 : 1;
    const nextLabel = nextStatus === 1 ? "active" : "inactive";
    const confirm = await Swal.fire({
      text: `Do you want to make this user ${nextLabel}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, make ${nextLabel}`,
      cancelButtonText: "Cancel",
      confirmButtonColor: "#405189",
      cancelButtonColor: "#878a99",
    });
    if (!confirm.isConfirmed) return;

    setStatusUpdatingId(row.ID);
    try {
      const res = await putAPI(
        `/urm/user/update-status/${row.ID}/${nextStatus}`
      );
      if (res.status === 1) {
        getList();
        Swal.fire({ text: res.message, timer: 2000 });
      } else {
        Swal.fire({
          text: res.message || "Could not update status",
          icon: "error",
          timer: 2500,
        });
      }
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const drawerOpen = displayFormMode !== "";

  return (
    <div>
      <Header />
      <div id="layout-wrapper">
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12 col-lg-12 col-xl-12 col-md-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <div>
                      <div className="page-title-right">
                        <ol className="breadcrumb m-0">
                          <li className="breadcrumb-item">URM</li>
                          <li className="breadcrumb-item active">users</li>
                        </ol>
                      </div>
                      <h4 className="mb-sm-0">Users</h4>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={openAddDrawer}
                    >
                      <i className="mdi mdi-plus"></i> Add New User
                    </button>
                  </div>
                </div>
              </div>

              <div className="row mt-2">
                <div className="col-xl-12 col-lg-12">
                  <div className="card">
                    <div className="card-body" style={{ padding: "10px" }}>
                      <div className="d-flex justify-content-end gap-3">
                        <div>
                          <input
                            type="text"
                            onKeyDown={(e) => setSearchKeyword(e.target.value)}
                            className="form-control"
                            placeholder="Search..."
                            id="searchKeyWord"
                          />
                        </div>
                        <div className="mb-3 d-flex align-items-center">
                          <button
                            className="btn btn-users-search"
                            onClick={() => getList()}
                          >
                            <i className="mdi mdi-magnify"></i> Go
                          </button>
                        </div>
                      </div>
                      <div className="table-responsive table-scroll-fixed">
                        <table className="table table-striped table-hover table_client">
                          <thead>
                            <tr
                              className="bg-primary-1"
                              style={{ color: "white" }}
                            >
                              <th scope="col">Sr. no.</th>
                              <th scope="col" style={{ textAlign: "left" }}>
                                Image
                              </th>
                              <th scope="col" style={{ textAlign: "left" }}>
                                Name
                              </th>
                              <th scope="col" style={{ textAlign: "left" }}>
                                Phone
                              </th>
                              <th scope="col" style={{ textAlign: "left" }}>
                                Email
                              </th>
                              <th scope="col" style={{ textAlign: "left" }}>
                                Role
                              </th>
                              <th scope="col" style={{ textAlign: "left" }}>
                                Team
                              </th>
                              <th scope="col" style={{ textAlign: "left" }}>
                                Status
                              </th>
                              <th scope="col" style={{ textAlign: "left" }}>
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="table-body">
                            {listLoading ? (
                              <tr>
                                <td colSpan={9} className="text-center py-4">
                                  <div
                                    className="spinner-border text-primary"
                                    role="status"
                                  >
                                    <span className="visually-hidden">
                                      Loading...
                                    </span>
                                  </div>
                                  <div className="mt-2 text-muted">Loading...</div>
                                </td>
                              </tr>
                            ) : users?.length > 0 ? (
                              <>
                                {users.map((row, index) => {
                                      const active = isUserActive(row);
                                      return (
                                        <tr key={row?.ID || index + 1} id={row?.ID}>
                                          <td>
                                            {(pageInfo.current_page - 1) *
                                              pageInfo.perPage +
                                              ++index}
                                          </td>
                                          <td style={{ textAlign: "left" }}>
                                            <img
                                              src={
                                                row.profileImg ||
                                                DEFAULT_USER_AVATAR
                                              }
                                              width="40"
                                              height="40"
                                              alt=""
                                              style={{
                                                borderRadius: "50%",
                                                objectFit: "cover",
                                              }}
                                              onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src =
                                                  DEFAULT_USER_AVATAR;
                                              }}
                                            />
                                          </td>
                                          <td style={{ textAlign: "left" }}>
                                            {row?.firstName !== null
                                              ? row?.firstName
                                              : "-"}{" "}
                                            {row?.lastName !== null
                                              ? row?.lastName
                                              : ""}
                                          </td>
                                          <td style={{ textAlign: "left" }}>
                                            {row?.phone &&
                                            row?.phone !== null &&
                                            row?.phone !== ""
                                              ? row?.phone
                                              : "-"}
                                          </td>
                                          <td style={{ textAlign: "left" }}>
                                            {row?.email}
                                          </td>
                                          <td style={{ textAlign: "left" }}>
                                            {row?.roleName}
                                          </td>
                                          <td style={{ textAlign: "left" }}>
                                            {row?.teamName || "-"}
                                          </td>
                                          <td style={{ textAlign: "left" }}>
                                            <div className="form-check form-switch form-switch-lg mb-0 user-status-switch">
                                              <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id={`user-status-${row.ID}`}
                                                checked={active}
                                                disabled={
                                                  statusUpdatingId === row.ID
                                                }
                                                onChange={() =>
                                                  updateUserStatus(row)
                                                }
                                                title={
                                                  active
                                                    ? "Active"
                                                    : "Inactive"
                                                }
                                              />
                                            </div>
                                          </td>
                                          <td style={{ textAlign: "left" }}>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-users-edit"
                                              onClick={() =>
                                                openEditDrawer(row)
                                              }
                                            >
                                              <i className="mdi mdi-pencil"></i>{" "}
                                              Edit
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                })}
                              </>
                            ) : (
                              <tr>
                                <td colSpan={9} className="text-center py-4 text-muted">
                                  No data found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <Pagination
                        pageInfo={pageInfo}
                        changePage={changePage}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {drawerOpen &&
        createPortal(
          <>
            <div
              className="user-drawer-backdrop"
              aria-hidden="true"
            />
            <div
              className="user-form-drawer"
              role="dialog"
              aria-modal="true"
              aria-labelledby="userFormDrawerLabel"
            >
              <div className="user-form-drawer-header">
                <h5 className="mb-0 text-truncate" id="userFormDrawerLabel">
                  {displayFormMode === "edit"
                    ? `Edit User - ${user.firstName || ""} ${user.lastName || ""}`
                    : "Add New User"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeDrawer}
                />
              </div>
              <div className="user-form-drawer-body">
                {errors?.saveUser && (
                  <div className="alert alert-danger py-2 mb-3" role="alert">
                    {errors.saveUser}
                  </div>
                )}
                <form
                  className="row g-3"
                  onSubmit={submitUser}
                  key={`${displayFormMode}-${user.ID || "new"}`}
                >
                  <div className="col-6">
                    <label className="form-label" htmlFor="firstName">
                      First name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter first name"
                      id="firstName"
                      name="firstName"
                      defaultValue={user.firstName}
                      required
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label" htmlFor="lastName">
                      Last name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter last name"
                      id="lastName"
                      name="lastName"
                      defaultValue={user.lastName}
                      required
                    />
                  </div>
                  <input
                    type="text"
                    style={{ display: "none" }}
                    autoComplete="username"
                  />
                  <div className="col-6">
                    <label className="form-label" htmlFor="email">
                      Email <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter email"
                      id="email"
                      name="email"
                      defaultValue={user.email}
                      required
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label" htmlFor="phone">
                      Phone
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter phone number"
                      id="phone"
                      name="phone"
                      defaultValue={user.phone}
                    />
                  </div>
                  <div className="col-12">
                    <input type="hidden" className="form-control" id="roleId" />
                    <label className="form-label" htmlFor="roleID">
                      Role <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      id="roleID"
                      name="roleID"
                      required
                      defaultValue={
                        user.roleID != null && user.roleID !== ""
                          ? String(user.roleID)
                          : ""
                      }
                    >
                      <option value={""}>Select role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={String(role.id)}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label" htmlFor="teamID">
                      Team
                    </label>
                    <select
                      className="form-select"
                      id="teamID"
                      name="teamID"
                      defaultValue={
                        user.teamID != null && user.teamID !== ""
                          ? String(user.teamID)
                          : ""
                      }
                    >
                      <option value={""}>Select team</option>
                      {teams.map((team) => (
                        <option key={team.id} value={String(team.id)}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">
                      Avatar
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <img
                        id="imageView"
                        className="user-avatar-field-preview"
                        src={avatarPreview || DEFAULT_USER_AVATAR}
                        alt=""
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = DEFAULT_USER_AVATAR;
                        }}
                      />
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleChange}
                        disabled={avatarUploading}
                      />
                    </div>
                    <small className="form-text text-muted">
                      Max size 1 MB. Image must not exceed 500 * 500 px.
                      {avatarUploading ? " Uploading..." : ""}
                    </small>
                    <input
                      type="hidden"
                      id="profileImg"
                      name="profileImg"
                      defaultValue={user.profileImg}
                    />
                  </div>
                  <input
                    type="password"
                    style={{ display: "none" }}
                    autoComplete="new-password"
                  />
                  <div className="col-12">
                    <label className="form-label" htmlFor="password">
                      Password
                      {displayFormMode === "add" && (
                        <span className="text-danger"> *</span>
                      )}
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      placeholder={
                        displayFormMode === "edit"
                          ? "Leave blank to keep current"
                          : "Enter password"
                      }
                      id="password"
                      name="password"
                      required={displayFormMode === "add"}
                      minLength={displayFormMode === "add" ? 8 : undefined}
                    />
                    <small className="form-text text-muted">
                      {displayFormMode === "edit"
                        ? "Leave blank to keep the current password. A new password replaces the old one."
                        : "Minimum 8 characters."}
                    </small>
                  </div>
                  <div className="col-12 mt-5">
                    <div className="d-flex align-items-center gap-3">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        style={{ minWidth: "110px" }}
                      >
                        {displayFormMode === "edit" ? "Update" : "Add"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-users-cancel"
                        style={{ minWidth: "110px" }}
                        id="cancelButton"
                        onClick={closeDrawer}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </>,
          document.body
        )}

      <Footer />
    </div>
  );
}

export default ActiveUser;
